import { useEffect } from "react";
import Banner from "@/components/Banner";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import useDeleteTask from "../_hooks/useDeleteTask";
import type { CRMTask } from "@/types";
import type { TaskAnalyticsSource } from "../_utils/amplitude";

interface DeleteTaskDialogProps {
  open: boolean;
  taskId: string;
  task?: CRMTask;
  source?: TaskAnalyticsSource;
  onClose: () => void;
  onDeleted: () => void;
}

export default function DeleteTaskDialog({ open, taskId, task, source, onClose, onDeleted }: DeleteTaskDialogProps) {
  const { deleteTask, loading, error, clearError } = useDeleteTask({ task, source });

  useEffect(() => {
    if (open) clearError();
  }, [open, clearError]);

  const handleConfirm = async () => {
    const success = await deleteTask(taskId);
    if (success) onDeleted();
  };

  return (
    <Dialog
      open={open}
      onClosed={onClose}
      headline="Delete Task"
      className="w-[490px] p-6 pr-2"
      actions={
        <>
          <Button variant="text" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="filled"
            theme="error"
            trailingIcon={false}
            icon="delete"
            onClick={handleConfirm}
            disabled={loading}
            testId="confirm-delete-task"
          >
            Delete
          </Button>
        </>
      }
    >
      {error && <Banner variant="error" description={error} inline closable={false} hideSecondaryDismiss />}
      <p>Are you sure you want to delete this task? This cannot be undone.</p>
    </Dialog>
  );
}
